import type { RefObject } from "react";

import {
  Box,
  Button,
  Callout,
  Flex,
  Inset,
  Spinner,
  Text,
} from "@radix-ui/themes";

import type {
  DataTableColumns,
  DataTableDensity,
} from "~/v3-poc/components/DataTable";
import { OrdersResultsTablePresentation } from "./OrdersResultsTable.pres";
import type { OrdersWidgetRow } from "./OrdersWidget.types";
import type { ReorderOrdersColumns } from "./useOrdersColumnVisibility";

export interface OrdersResultsPresentationProps {
  rows: OrdersWidgetRow[];
  columns: DataTableColumns<OrdersWidgetRow>;
  tableMinWidth: string;
  density: DataTableDensity;
  horizontalLines: boolean;
  verticalLines: boolean;
  resultsRegionRef: RefObject<HTMLDivElement>;
  pageContextId: string;
  rowCount: number;
  selectedOrderId?: string;
  batchSelectedIds: readonly string[];
  contained: boolean;
  isAccountReady: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  isError: boolean;
  isRefreshError: boolean;
  errorMessage?: string;
  hasAppliedFilters: boolean;
  appliedTimeWindow: string;
  onReset: () => void;
  onRetry: () => void;
  onLoadMore: () => void;
  onRowActivate: (
    row: OrdersWidgetRow,
    rowElement: HTMLTableRowElement,
  ) => void;
  onReorderColumns: ReorderOrdersColumns;
}

const TIME_WINDOW_EMPTY_RANGES: Record<string, string> = {
  "Last 24 hours": "the last 24 hours",
  "Last 3 days": "the last 3 days",
  "Last 7 days": "the last 7 days",
  "Last 1 month": "the last 1 month",
};

const getOrdersEmptyMessage = ({
  isAccountReady,
  hasAppliedFilters,
  appliedTimeWindow,
}: Pick<
  OrdersResultsPresentationProps,
  "isAccountReady" | "hasAppliedFilters" | "appliedTimeWindow"
>): string => {
  if (!isAccountReady) {
    return "Select an account to view orders.";
  }
  const timeRange = TIME_WINDOW_EMPTY_RANGES[appliedTimeWindow];
  if (hasAppliedFilters) {
    return timeRange
      ? `No orders match the applied filters in ${timeRange}.`
      : "No orders match the applied filters.";
  }
  return timeRange ? `No orders in ${timeRange}.` : "No orders.";
};

const getResultsInsetClassName = (contained: boolean): string | undefined =>
  contained ? "contents" : undefined;

const getContainedResultsLayoutProps = (contained: boolean) =>
  contained
    ? {
        flexGrow: "1" as const,
        height: "100%" as const,
        minHeight: "0" as const,
        px: "2" as const,
      }
    : {};

const getContainedScrollLayoutProps = (contained: boolean) =>
  contained
    ? {
        height: "100%" as const,
        minHeight: "0" as const,
      }
    : {};

const OrdersResultsLoading = () => (
  <Flex align="center" justify="center" gap="2" py="5" role="status">
    <Spinner />
    <Text size="2" color="gray">
      Loading orders…
    </Text>
  </Flex>
);

const OrdersResultsError = ({
  errorMessage,
  isFetching,
  onRetry,
}: {
  errorMessage?: string;
  isFetching: boolean;
  onRetry: () => void;
}) => (
  <Callout.Root color="red" role="alert">
    <Callout.Text>{errorMessage ?? "Orders could not be loaded."}</Callout.Text>
    <Button
      type="button"
      size="1"
      variant="soft"
      color="red"
      onClick={onRetry}
      loading={isFetching}
    >
      Retry
    </Button>
  </Callout.Root>
);

const OrdersResultsBody = ({
  rows,
  columns,
  tableMinWidth,
  density,
  horizontalLines,
  verticalLines,
  selectedOrderId,
  batchSelectedIds,
  isAccountReady,
  isLoading,
  isFetching,
  isError,
  errorMessage,
  hasAppliedFilters,
  appliedTimeWindow,
  onReset,
  onRetry,
  onRowActivate,
  onReorderColumns,
  onViewportScroll,
}: Omit<
  OrdersResultsPresentationProps,
  | "resultsRegionRef"
  | "pageContextId"
  | "rowCount"
  | "contained"
  | "isRefreshError"
  | "hasNextPage"
  | "isFetchingNextPage"
  | "onLoadMore"
> & {
  onViewportScroll?: (event: Event) => void;
}) => {
  if (isLoading && rows.length === 0) {
    return <OrdersResultsLoading />;
  }
  if (isError && rows.length === 0) {
    return (
      <OrdersResultsError
        errorMessage={errorMessage}
        isFetching={isFetching}
        onRetry={onRetry}
      />
    );
  }

  return (
    <OrdersResultsTablePresentation
      rows={rows}
      columns={columns}
      tableMinWidth={tableMinWidth}
      density={density}
      horizontalLines={horizontalLines}
      verticalLines={verticalLines}
      selectedOrderId={selectedOrderId}
      batchSelectedIds={batchSelectedIds}
      isAccountReady={isAccountReady}
      hasAppliedFilters={hasAppliedFilters}
      emptyMessage={getOrdersEmptyMessage({
        isAccountReady,
        hasAppliedFilters,
        appliedTimeWindow,
      })}
      onReset={onReset}
      onRowActivate={onRowActivate}
      onReorderColumns={onReorderColumns}
      onViewportScroll={onViewportScroll}
    />
  );
};

const OrdersStatusCount = ({
  rowCount,
  pageContextId,
  isAccountReady,
  isLoading,
  isError,
}: {
  rowCount: number;
  pageContextId: string;
  isAccountReady: boolean;
  isLoading: boolean;
  isError: boolean;
}) => {
  if (isLoading || !isAccountReady || (isError && rowCount === 0)) {
    return null;
  }

  return (
    <Text id={pageContextId} size="1" color="gray" className="atd-numeric">
      {rowCount} {rowCount === 1 ? "order" : "orders"}
    </Text>
  );
};

export const OrdersResultsPresentation = ({
  rows,
  columns,
  tableMinWidth,
  density,
  horizontalLines,
  verticalLines,
  resultsRegionRef,
  pageContextId,
  rowCount,
  selectedOrderId,
  batchSelectedIds,
  contained,
  isAccountReady,
  isLoading,
  isFetching,
  isFetchingNextPage,
  hasNextPage,
  isError,
  isRefreshError,
  errorMessage,
  hasAppliedFilters,
  appliedTimeWindow,
  onReset,
  onRetry,
  onLoadMore,
  onRowActivate,
  onReorderColumns,
}: OrdersResultsPresentationProps) => {
  const showRefreshError = (isRefreshError || isError) && rows.length > 0;

  const handleViewportScroll = (event: Event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = target;
    if (
      scrollHeight - scrollTop - clientHeight < 80 &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isLoading
    ) {
      onLoadMore();
    }
  };

  return (
    <>
      <Flex
        data-testid="orders-results"
        direction="column"
        gap="2"
        {...getContainedResultsLayoutProps(contained)}
      >
        {showRefreshError ? (
          <Callout.Root color="red" role="alert">
            <Callout.Text>Couldn&apos;t refresh orders.</Callout.Text>
            <Button
              type="button"
              size="1"
              variant="soft"
              color="red"
              onClick={onRetry}
              loading={isFetching}
            >
              Retry
            </Button>
          </Callout.Root>
        ) : null}

        <Inset side="x" className={getResultsInsetClassName(contained)}>
          <Box
            ref={resultsRegionRef}
            data-testid="orders-table-scroll"
            className="orders-table-scroll"
            role="region"
            aria-label="Orders results"
            aria-describedby={pageContextId}
            aria-busy={isLoading || isFetchingNextPage}
            tabIndex={-1}
            maxWidth="100%"
            {...getContainedScrollLayoutProps(contained)}
          >
            <OrdersResultsBody
              rows={rows}
              columns={columns}
              tableMinWidth={tableMinWidth}
              density={density}
              horizontalLines={horizontalLines}
              verticalLines={verticalLines}
              selectedOrderId={selectedOrderId}
              batchSelectedIds={batchSelectedIds}
              isAccountReady={isAccountReady}
              isLoading={isLoading}
              isFetching={isFetching}
              isError={isError}
              errorMessage={errorMessage}
              hasAppliedFilters={hasAppliedFilters}
              appliedTimeWindow={appliedTimeWindow}
              onReset={onReset}
              onRetry={onRetry}
              onRowActivate={onRowActivate}
              onReorderColumns={onReorderColumns}
              onViewportScroll={handleViewportScroll}
            />
          </Box>
        </Inset>
      </Flex>
      <Box
        data-testid="orders-pagination"
        flexShrink="0"
        px={contained ? "2" : undefined}
        pb={contained ? "2" : undefined}
      >
        <Flex align="center" justify="between" gap="2" wrap="wrap">
          <OrdersStatusCount
            rowCount={rowCount}
            pageContextId={pageContextId}
            isAccountReady={isAccountReady}
            isLoading={isLoading}
            isError={isError}
          />
        </Flex>
      </Box>
    </>
  );
};
